import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GlobeAltIcon,
  ScaleIcon,
  FlagIcon,
  PlusCircleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/solid";
import { AuthContext } from "../context/AuthContext";
import {
  getMyProfile,
  updateMyProfile,
  completeOnboarding,
} from "../api/usersApi";
import {
  createManualAsset,
  TYPE_TO_ASSET_CLASS,
} from "../api/manualAssetsApi";
import {
  getSnapTradeAuthLink,
  registerSnapTradeUser,
} from "../api/snaptrade";

const COUNTRIES = [
  { code: "IN", flag: "🇮🇳", name: "India", tax: "STCG/LTCG, Section 80C" },
  { code: "US", flag: "🇺🇸", name: "United States", tax: "Cap gains, IRA limits" },
  { code: "GB", flag: "🇬🇧", name: "United Kingdom", tax: "" },
  { code: "CA", flag: "🇨🇦", name: "Canada", tax: "" },
  { code: "AU", flag: "🇦🇺", name: "Australia", tax: "" },
  { code: "SG", flag: "🇸🇬", name: "Singapore", tax: "" },
  { code: "AE", flag: "🇦🇪", name: "UAE", tax: "" },
  { code: "OTHER", flag: "🌍", name: "Other / not listed", tax: "" },
];

const RISK_OPTIONS = [
  {
    value: "conservative",
    label: "Conservative",
    blurb: "I want capital preservation and stable income. Volatility makes me nervous.",
  },
  {
    value: "moderate",
    label: "Moderate",
    blurb: "Balanced — I want growth but I sleep at night during normal corrections.",
  },
  {
    value: "aggressive",
    label: "Aggressive",
    blurb: "Maximize long-term growth. I can stomach big drawdowns for higher upside.",
  },
];

const GOAL_TEMPLATES = [
  { label: "🏖️  Retire comfortably", template: "Retire comfortably" },
  { label: "🏠  Buy a house in 3-5 years", template: "Buy a house in 3-5 years" },
  { label: "🎓  Fund a child's education", template: "Fund a child's education" },
  { label: "🚀  Reach FIRE (Financial Independence, Retire Early)", template: "Reach FIRE" },
  { label: "💰  Build an emergency fund", template: "Build an emergency fund" },
  { label: "📈  Grow long-term wealth", template: "Grow long-term wealth" },
];

const ASSET_TYPES = [
  { label: "Real Estate / Land", value: "Real Estate / Land" },
  { label: "Gold / Commodities", value: "Gold / Commodities" },
  { label: "Cash / Savings", value: "Cash / Savings" },
  { label: "Crypto", value: "Crypto" },
  { label: "Private Business", value: "Private Business" },
  { label: "Stocks (paper / off-broker)", value: "Stocks" },
  { label: "Other", value: "Other" },
];

const Step = ({ icon: Icon, eyebrow, title, subtitle, children }) => (
  <div className="space-y-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-indigo-500/15 text-indigo-500 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {eyebrow}
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold leading-tight">{title}</h2>
      </div>
    </div>
    {subtitle && (
      <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
    )}
    {children}
  </div>
);

const Welcome = () => {
  const navigate = useNavigate();
  const { refreshProfile } = useContext(AuthContext);

  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Step state
  const [country, setCountry] = useState("");
  const [risk, setRisk] = useState("");
  const [horizon, setHorizon] = useState("");
  const [goals, setGoals] = useState([]);
  const [customGoal, setCustomGoal] = useState("");
  const [assetSkipped, setAssetSkipped] = useState(false);
  const [manualDraft, setManualDraft] = useState({
    name: "",
    type: "Real Estate / Land",
    current_value: "",
    cost_basis: "",
    currency: "USD",
  });

  useEffect(() => {
    (async () => {
      try {
        const p = await getMyProfile();
        setProfile(p);
        if (p.onboarding_completed) {
          navigate("/portfolio", { replace: true });
          return;
        }
        // Pre-fill anything the user already has set.
        setCountry(p.country || "");
        setRisk(p.risk_tolerance || "");
        setHorizon(p.time_horizon ?? "");
        setGoals(Array.isArray(p.goals) ? p.goals : []);
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate]);

  const totalSteps = 4;
  const progress = ((step + 1) / totalSteps) * 100;

  const flashError = (msg) => {
    setError(msg);
    setTimeout(() => setError(""), 5000);
  };

  // ---------------- Step actions ----------------

  const saveStep1 = async () => {
    if (!country) {
      flashError("Pick where you live so the Council knows the right tax rules.");
      return false;
    }
    const tax = country === "OTHER" ? "" : country;
    setBusy(true);
    try {
      await updateMyProfile({
        country: country === "OTHER" ? "" : country,
        tax_jurisdiction: tax,
      });
      return true;
    } catch {
      flashError("Could not save. Try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveStep2 = async () => {
    if (!risk) {
      flashError("Pick a risk tolerance so we can tune advice.");
      return false;
    }
    setBusy(true);
    try {
      await updateMyProfile({
        risk_tolerance: risk,
        time_horizon: horizon ? Number(horizon) : null,
      });
      return true;
    } catch {
      flashError("Could not save.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveStep3 = async () => {
    const merged = [
      ...goals,
      ...(customGoal.trim() ? [{ label: customGoal.trim() }] : []),
    ];
    setBusy(true);
    try {
      await updateMyProfile({ goals: merged });
      setGoals(merged);
      setCustomGoal("");
      return true;
    } catch {
      flashError("Could not save goals.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveStep4 = async () => {
    // If they're skipping the asset add, just proceed.
    if (assetSkipped) return true;

    const hasManual = manualDraft.name.trim() && manualDraft.current_value;
    if (!hasManual) {
      // Empty form is fine — equivalent to skip.
      return true;
    }
    setBusy(true);
    try {
      await createManualAsset({
        name: manualDraft.name.trim(),
        asset_class: TYPE_TO_ASSET_CLASS[manualDraft.type] || "other",
        current_value: Number(manualDraft.current_value) || 0,
        cost_basis: Number(manualDraft.cost_basis) || Number(manualDraft.current_value) || 0,
        currency: manualDraft.currency,
      });
      return true;
    } catch {
      flashError("Could not save the asset, but you can add it later.");
      return true; // don't block them
    } finally {
      setBusy(false);
    }
  };

  const onLinkBrokerage = async () => {
    setBusy(true);
    try {
      // First call may 404 if the user hasn't been registered with SnapTrade
      // yet — try registration once and retry. Mirrors LinkedAccounts.jsx.
      let res;
      try {
        res = await getSnapTradeAuthLink();
      } catch (err) {
        if (err.response?.status === 404 || err.response?.status === 400) {
          await registerSnapTradeUser();
          res = await getSnapTradeAuthLink();
        } else {
          throw err;
        }
      }
      const authUrl = res?.data?.auth_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        flashError("Couldn't open the brokerage portal. Try again later.");
      }
    } catch {
      flashError("Could not start linking. You can do this later from Linked Accounts.");
    } finally {
      setBusy(false);
    }
  };

  const next = async () => {
    let ok = true;
    if (step === 0) ok = await saveStep1();
    else if (step === 1) ok = await saveStep2();
    else if (step === 2) ok = await saveStep3();
    else if (step === 3) ok = await saveStep4();
    if (!ok) return;
    if (step === totalSteps - 1) {
      setBusy(true);
      try {
        await completeOnboarding();
        await refreshProfile?.();
        navigate("/portfolio", { replace: true });
      } catch (err) {
        console.error("complete-onboarding failed:", err);
        const status = err?.response?.status;
        const detail =
          err?.response?.data?.detail ||
          (status === 404
            ? "Endpoint not found — restart your Django dev server so it picks up /api/users/me/complete-onboarding/."
            : status === 401
              ? "Session expired. Sign in again."
              : `Could not complete onboarding (HTTP ${status ?? "network"}).`);
        flashError(detail);
      } finally {
        setBusy(false);
      }
      return;
    }
    setStep((s) => s + 1);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const toggleGoal = (g) => {
    setGoals((curr) =>
      curr.find((x) => x.label === g.template)
        ? curr.filter((x) => x.label !== g.template)
        : [...curr, { label: g.template }],
    );
  };

  const goalActive = (g) => goals.some((x) => x.label === g.template);

  // ---------------- Render ----------------

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        {/* Header / progress */}
        <div className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500 mb-1">
            Welcome to FinAI
          </p>
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            Let's set up the Council so it actually knows{" "}
            <span className="text-indigo-500">you</span>.
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            4 quick steps. You can change any of this later in Settings.
          </p>
          <div className="mt-5 h-1.5 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-indigo-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Step {step + 1} of {totalSteps}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-3 rounded-lg bg-red-500/10 text-red-500 border border-red-500/40 text-sm">
            {error}
          </div>
        )}

        <div className="p-6 sm:p-8 rounded-2xl bg-white/70 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark">
          {step === 0 && (
            <Step
              icon={GlobeAltIcon}
              eyebrow="Step 1 · Where you invest"
              title="Where do you live?"
              subtitle="The Tally agent applies your country's tax rules (capital gains, allowances) to its advice. Pick the country whose tax system applies to you."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {COUNTRIES.map((c) => {
                  const active = country === c.code;
                  return (
                    <button
                      key={c.code}
                      onClick={() => setCountry(c.code)}
                      className={`text-left p-3 rounded-xl border transition flex items-center gap-3 ${
                        active
                          ? "border-indigo-500 bg-indigo-500/10"
                          : "border-gray-300 dark:border-gray-700 hover:border-indigo-500/50"
                      }`}
                    >
                      <span className="text-2xl">{c.flag}</span>
                      <span className="flex-1 min-w-0">
                        <p className="font-bold">{c.name}</p>
                        {c.tax && (
                          <p className="text-xs text-gray-500 truncate">{c.tax}</p>
                        )}
                      </span>
                      {active && (
                        <CheckCircleIcon className="w-5 h-5 text-indigo-500 shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </Step>
          )}

          {step === 1 && (
            <Step
              icon={ScaleIcon}
              eyebrow="Step 2 · Risk & horizon"
              title="How do you feel about risk?"
              subtitle="Compass uses this to weight allocation advice. There's no right answer."
            >
              <div className="grid grid-cols-1 gap-2">
                {RISK_OPTIONS.map((r) => {
                  const active = risk === r.value;
                  return (
                    <button
                      key={r.value}
                      onClick={() => setRisk(r.value)}
                      className={`text-left p-4 rounded-xl border transition ${
                        active
                          ? "border-indigo-500 bg-indigo-500/10"
                          : "border-gray-300 dark:border-gray-700 hover:border-indigo-500/50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-bold">{r.label}</p>
                        {active && <CheckCircleIcon className="w-5 h-5 text-indigo-500" />}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{r.blurb}</p>
                    </button>
                  );
                })}
              </div>

              <div className="mt-2">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1.5">
                  Time horizon (optional)
                </p>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={horizon}
                  onChange={(e) => setHorizon(e.target.value)}
                  placeholder="e.g. 20 (years)"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  How many years until you'll need this money. Skip if not sure.
                </p>
              </div>
            </Step>
          )}

          {step === 2 && (
            <Step
              icon={FlagIcon}
              eyebrow="Step 3 · Goals"
              title="What are you investing for?"
              subtitle="Pick anything that fits. The Council will tie advice back to these."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {GOAL_TEMPLATES.map((g) => {
                  const active = goalActive(g);
                  return (
                    <button
                      key={g.template}
                      onClick={() => toggleGoal(g)}
                      className={`text-left p-3 rounded-xl border transition flex items-center justify-between ${
                        active
                          ? "border-indigo-500 bg-indigo-500/10"
                          : "border-gray-300 dark:border-gray-700 hover:border-indigo-500/50"
                      }`}
                    >
                      <span className="font-medium">{g.label}</span>
                      {active && <CheckCircleIcon className="w-5 h-5 text-indigo-500" />}
                    </button>
                  );
                })}
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-1.5">
                  Or write your own
                </p>
                <input
                  value={customGoal}
                  onChange={(e) => setCustomGoal(e.target.value)}
                  placeholder="Save 50L for kid's college by 2035"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </Step>
          )}

          {step === 3 && (
            <Step
              icon={PlusCircleIcon}
              eyebrow="Step 4 · Get something in there"
              title="Add at least one asset"
              subtitle="Without holdings there's nothing for the Council to reason about. Link a brokerage or add anything you own."
            >
              <div className="p-4 rounded-xl border border-indigo-500/40 bg-indigo-500/5 flex items-center justify-between gap-3">
                <div>
                  <p className="font-bold">Link a brokerage</p>
                  <p className="text-xs text-gray-500">
                    Best option — pulls real positions automatically (SnapTrade).
                  </p>
                </div>
                <button
                  onClick={onLinkBrokerage}
                  disabled={busy}
                  className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  Link
                </button>
              </div>

              <p className="text-center text-xs text-gray-500 my-2">— or add something manually —</p>

              <div className="space-y-3">
                <input
                  value={manualDraft.name}
                  onChange={(e) => setManualDraft({ ...manualDraft, name: e.target.value })}
                  placeholder='Asset name (e.g. "Apartment, Hyderabad")'
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={manualDraft.type}
                    onChange={(e) => setManualDraft({ ...manualDraft, type: e.target.value })}
                    className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {ASSET_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={manualDraft.currency}
                    onChange={(e) => setManualDraft({ ...manualDraft, currency: e.target.value })}
                    className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {["USD", "INR", "EUR", "GBP", "AED", "SGD"].map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    inputMode="decimal"
                    value={manualDraft.cost_basis}
                    onChange={(e) =>
                      setManualDraft({ ...manualDraft, cost_basis: e.target.value })
                    }
                    placeholder="What you paid"
                    className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    inputMode="decimal"
                    value={manualDraft.current_value}
                    onChange={(e) =>
                      setManualDraft({ ...manualDraft, current_value: e.target.value })
                    }
                    placeholder="What it's worth now"
                    className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <button
                onClick={async () => {
                  setAssetSkipped(true);
                  setBusy(true);
                  try {
                    await completeOnboarding();
                    await refreshProfile?.();
                    navigate("/portfolio", { replace: true });
                  } catch (err) {
                    console.error("complete-onboarding failed:", err);
                    const status = err?.response?.status;
                    flashError(
                      err?.response?.data?.detail ||
                        (status === 404
                          ? "Endpoint not found — restart your Django dev server."
                          : status === 401
                            ? "Session expired. Sign in again."
                            : `Could not complete onboarding (HTTP ${status ?? "network"}).`),
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
                disabled={busy}
                className="text-xs text-gray-500 hover:text-indigo-500 disabled:opacity-50"
              >
                Skip for now — I'll add assets later →
              </button>
            </Step>
          )}
        </div>

        {/* Footer nav */}
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={back}
            disabled={step === 0 || busy}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={next}
            disabled={busy}
            className={`inline-flex items-center gap-1 px-5 py-2.5 rounded-lg text-white text-sm font-semibold ${
              busy ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {step === totalSteps - 1 ? "Finish setup" : "Continue"}
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
